// PatientPortalLayout — the shell mounted under /patient-home/*.
//
// Responsibilities:
//   1. Wrap everything in patientTheme via styled-components ThemeProvider.
//   2. Mount PatientProvider (multi-patient identity context).
//   3. Decide between three top-level states based on profile count:
//      - 0 profiles  → onboarding stub (will become real intake in Phase F7)
//      - currentProfile is null but profiles.length > 1 → profile picker page
//      - currentProfile resolved → render the portal (top bar + rail + routes)
//
// The "currentProfile is null with N profiles" state happens on first ever
// load when no last-selected is stored in localStorage. After picking once
// the choice is remembered.

import React, { useEffect, useState } from 'react'
import { ThemeProvider } from 'styled-components'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import patientTheme from '../theme/patientTheme'
import { PatientProvider, usePatient } from '../context/PatientContext'
import PatientTopBar from './PatientTopBar'
import PatientLeftRail from './PatientLeftRail'
import PatientBottomNav from './PatientBottomNav'
import PatientRoutesHandler from './PatientRoutesHandler'
import PatientProfilePickerPage from '../pages/picker/PatientProfilePickerPage'
import PatientIntakeFlow from '../pages/onboarding/PatientIntakeFlow'
import { PatientGlobalStyle, PortalShell } from './styledComponents'

function PatientPortalInner() {
  const { profiles, currentProfile, loading } = usePatient()
  // Default: expanded on desktop, closed (drawer hidden) on mobile so it
  // doesn't take over the screen on first paint.
  const [railOpen, setRailOpen] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.matchMedia('(min-width: 768px)').matches
  })

  // Mark <body> so global styles can scope and so print stylesheets later
  // can target the patient portal specifically.
  useEffect(() => {
    document.body.setAttribute('data-portal', 'patient')
    return () => {
      document.body.removeAttribute('data-portal')
    }
  }, [])

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingSpinner />
      </div>
    )
  }

  if (profiles.length === 0) {
    return <PatientIntakeFlow />
  }

  if (!currentProfile) {
    return <PatientProfilePickerPage />
  }

  return (
    <PortalShell className="patient-portal-root">
      <PatientTopBar onMenuClick={() => setRailOpen((v) => !v)} />
      <PatientLeftRail open={railOpen} onClose={() => setRailOpen(false)} />
      <PatientRoutesHandler railOpen={railOpen} />
      <PatientBottomNav />
    </PortalShell>
  )
}

export default function PatientPortalLayout() {
  return (
    <ThemeProvider theme={patientTheme}>
      <PatientGlobalStyle />
      <PatientProvider>
        <PatientPortalInner />
      </PatientProvider>
    </ThemeProvider>
  )
}
